import { PlusOutlined } from '@ant-design/icons';
import { DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import { DragHandle, Row } from '@/components/Drag';
import Filter from '@/components/Filter';
import Table from '@/components/list/Table';
import { useOrderingMarketingProducts } from '@/hooks/mutations/admin';
import { useAdminProducts } from '@/hooks/queries/admin';
import usePagination from '@/hooks/usePagination';
import type { GetAdminProductsResponse } from '@/types/admin';
import { getQueryKey } from '@/utils/queryKeys';

const ProductList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pagination = usePagination(['isPublished']);
  const query = useAdminProducts({ ...pagination.query });
  const [dataSource, setDataSource] = useState<
    GetAdminProductsResponse['results']
  >([]);
  const orderingMarketingProducts = useOrderingMarketingProducts({
    onSuccess: () => {
      message.success('순서가 저장되었어요');
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getProducts'),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProducts'),
      });
    },
    onError: () => {
      message.error('새로고침 후에 다시 시도해주세요');
    },
  });

  useEffect(() => {
    setDataSource(query.data?.results || []);
  }, [query.data?.results]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (pagination.query.isPublished !== undefined) {
      message.error('게시 상태를 초기화하고 순서를 변경해주세요');
    } else if (active.id !== over?.id) {
      const newDataSource = arrayMove(
        dataSource,
        dataSource.findIndex((record) => record.id === active?.id),
        dataSource.findIndex((record) => record.id === over?.id)
      );

      setDataSource(newDataSource);

      orderingMarketingProducts.mutate({
        page: pagination.query.page,
        size: pagination.query.size,
        ids: newDataSource.map((item) => item.id),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProducts'),
      });
    }
  };

  return (
    <div css={tw`flex flex-col gap-[16px]`}>
      <Filter
        pagination={pagination}
        filters={[
          {
            label: '게시 상태',
            name: 'isPublished',
            type: 'select',
            placeholder: '전체',
            options: {
              true: '게시중',
              false: '중지',
            },
            format: (value) => value === 'true',
          },
        ]}
      />

      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            headerConfig={{
              showTotalCount: true,
              showSizeSelector: true,
              extra: (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    navigate(PATH.MARKETING_PRODUCT_MANAGE.REGISTER);
                  }}
                >
                  상품추가
                </Button>
              ),
            }}
            columns={[
              {
                dataIndex: 'drag',
                align: 'center',
                width: 60,
                render: () => <DragHandle />,
              },
              { dataIndex: 'id', title: 'ID', width: 70 },
              {
                dataIndex: 'name',
                title: '상품명',
                width: 'auto',
              },
              {
                dataIndex: 'price',
                title: '정가',
                width: 110,
                align: 'right',
                render: (_, record) => `${record.price.toLocaleString()}원`,
              },
              {
                dataIndex: 'discountPrice',
                title: '할인가',
                width: 110,
                align: 'right',
                render: (_, record) =>
                  `${record.discountPrice.toLocaleString()}원`,
              },
              {
                key: 'resultPrice',
                title: '판매가',
                width: 110,
                align: 'right',
                render: (_, record) =>
                  `${(record.price - record.discountPrice).toLocaleString()}원`,
              },
              {
                dataIndex: 'cost',
                title: '원가',
                width: 110,
                align: 'right',
                render: (_, record) => `${record.cost.toLocaleString()}원`,
              },
              {
                dataIndex: 'isPublished',
                title: '상태',
                width: 85,
                align: 'center',
                render: (value) => (
                  <Badge
                    status={value ? 'success' : 'error'}
                    text={value ? '게시중' : '중지'}
                  />
                ),
              },
              {
                dataIndex: 'updatedAt',
                title: '업데이트일',
                width: 175,
                render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
              },
            ]}
            emptyText="등록된 상품이 없어요"
            dataSource={dataSource}
            rowKey={(row) => row.id}
            pagination={{
              total: query.data?.count,
              ...pagination,
            }}
            components={{ body: { row: Row } }}
            onRow={(record) => ({
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                navigate(`${PATH.MARKETING_PRODUCT_MANAGE.INDEX}/${record.id}`);
              },
            })}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ProductList;
